import { portalAPI, portalURL } from '@/helpers/environmentHelper';
import { logToServer } from '@/services/LogService';
import useAbortController from '@/utils/hooks/useAbortController';
import { useCurrency } from '@/utils/hooks/useCurrency';
import useCurrencyOptions from '@/utils/hooks/useCurrencyOptions';
import { useCurrencySymbol } from '@/utils/hooks/useCurrencySymbol';
import useLanguage from '@/utils/hooks/useLanguage';
import { use401Error } from '@/utils/hooks/usePortalAuthentication';
import useToast from '@/utils/hooks/useToast';
import { useSso } from '@/utils/providers/SSOProvider';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDebouncedValue } from '@mantine/hooks';
import axios from 'axios';
import BigNumber from 'bignumber.js';
import cn from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { useNumberFormatter } from 'react-aria';
import { FieldErrors, FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Button } from '../common/Button';
import Divider from '../common/Divider';
import PortalEntry from '../common/PortalEntry';
import { getDefaultCurrencyId } from '../form/schema/common';
import {
    PortalInternalTransferFormInput,
    PortalInternalTransferFormValues,
    portalInternalTransferSchema
} from '../form/schema/portalInternalTransferSchema';
import ErrorMessage from '../inputs/ErrorMessage';
import FilterSelect from '../inputs/FilterSelect';
import PortalInputController from '../inputs/PortalInputController';
import PortalNumberInput from '../inputs/PortalNumberInput';
import Select from '../inputs/Select';
import TextArea from '../inputs/TextArea';
import Modal, { ModalClose, ModalHeader, ModalProps, ModalTitle } from './Modal';

type Option = {
    label: string;
    value: string;
};

export enum TransferType {
    FIAT = 'FIAT',
    CRYPTO = 'CRYPTO'
}

export const transferTypeOptions: Option[] = [
    {
        label: TransferType.FIAT,
        value: TransferType.FIAT
    },
    {
        label: TransferType.CRYPTO,
        value: TransferType.CRYPTO
    }
];

const defaultCounterPartId = { label: 'Select an transfer counterpart', value: undefined };
const defaultCurrencyId = getDefaultCurrencyId('transfer', 'currency');
const defaultAssetId = getDefaultCurrencyId('transfer', 'asset');
const defaultInternalTransferType = transferTypeOptions[0];

const defaultValues: Partial<PortalInternalTransferFormInput> = {
    amount: undefined,
    transferType: defaultInternalTransferType,
    counterPartId: defaultCounterPartId,
    currencyId: defaultCurrencyId,
    requestDescription: ''
};

interface PortalInternalTransferModalProps extends ModalProps {
    onRefresh?: () => void;
}

const PortalInternalTransferModal = (props: PortalInternalTransferModalProps) => {
    const { opened, handlers, onRefresh } = props;

    const lang = useLanguage();
    const { jwt, foundEntity, balancesApi, linkedEntitiesApi } = useSso();
    const { on401Error } = use401Error();

    const [error, setError] = useState<any>({ message: '' });
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [toastError, toastSuccess] = useToast();

    const steps = useMemo(() => {
        return [{ key: 1, label: lang.portal.requestDetails }];
    }, [lang]);

    const formInstance = useForm<PortalInternalTransferFormInput>({
        defaultValues,
        mode: 'onChange',
        resolver: yupResolver(portalInternalTransferSchema())
    });

    const {
        reset,
        watch,
        setValue,
        setError: setFormError,
        handleSubmit,
        formState: { isSubmitting, isValid }
    } = formInstance;

    const [amount, selectedTransferType, selectedCounterPartId, selectedCurrency] = watch([
        'amount',
        'transferType',
        'counterPartId',
        'currencyId'
    ]);

    const { data: balances } = balancesApi;
    const { data: linkedEntities } = linkedEntitiesApi;

    const { transferCcyOptions } = useCurrencyOptions(selectedTransferType);
    const { abortController, setAbortController, onClose } = useAbortController({
        isLoading,
        handlers
    });

    const counterPartOptions = useMemo(() => {
        return linkedEntities
            ? linkedEntities
                  .filter((entity) => {
                      return entity.entityId !== foundEntity?.entityId;
                  })
                  .map((entity) => ({
                      label: entity.entityName,
                      value: entity.entityId
                  }))
            : [];
    }, [linkedEntities, foundEntity]);

    useEffect(() => {
        if (counterPartOptions && counterPartOptions.length === 1) {
            setValue('counterPartId', counterPartOptions[0]);
        } else {
            setValue('counterPartId', defaultCounterPartId);
        }
    }, [counterPartOptions]);

    const symbol = useCurrencySymbol(selectedCurrency?.name || '');
    const currencyConfig = useCurrency(selectedCurrency?.name || '');

    const balanceFormatter = useNumberFormatter({
        minimumFractionDigits: currencyConfig?.order_decimals > 1 ? currencyConfig?.order_decimals - 2 : 0,
        maximumFractionDigits: currencyConfig?.order_decimals || 0
    });

    const currentBalance = useMemo(() => {
        return balances?.find((balance: any) => balance.currencyName === selectedCurrency?.label)?.balance || 0;
    }, [balances, selectedCurrency]);

    const [debouncedAmount] = useDebouncedValue(amount, 150);
    const [debouncedBalance] = useDebouncedValue(currentBalance, 150);

    useEffect(() => {
        if (debouncedAmount && debouncedBalance) {
            if (BigNumber(debouncedAmount).isGreaterThan(debouncedBalance)) {
                setFormError('amount', {
                    type: 'manual',
                    message: `Amount exceeded available balance.`
                });
            }
        }
    }, [debouncedAmount, debouncedBalance]);

    const availableBalance = useMemo(() => {
        let balance;
        if (currentBalance) {
            return (
                <div
                    className={cn('flex items-center gap-1', {
                        'text-brand-green-light': currentBalance > 0,
                        'text-brand-red-light': currentBalance < 0
                    })}>
                    {balanceFormatter.format(currentBalance)}
                </div>
            );
        } else balance = `No available balance`;

        return selectedCurrency?.name ? balance : 'Select a transfer currency';
    }, [currentBalance, balanceFormatter, selectedCurrency, symbol, currencyConfig]);

    const onSubmit = async (data: PortalInternalTransferFormInput) => {
        setIsLoading(true);
        const dataValidated = data as PortalInternalTransferFormValues;

        try {
            if (foundEntity) {
                const payload = {
                    entityId: foundEntity.entityId,
                    amount: dataValidated.amount || 0,
                    requestDescription: dataValidated.requestDescription || null,
                    currencyId: dataValidated.currencyId.value,
                    counterpartEntityId: dataValidated.counterPartId.value
                };
                if (portalURL) {
                    await axios.post(portalAPI('/api/requests/internal-transfer'), payload, {
                        headers: { Authorization: `Bearer ${jwt}` },
                        signal: abortController.signal
                    });
                }
                const title = 'Internal Transfer';
                const body = 'Internal Transfer request submitted successfully';
                toastSuccess({ body, title });
                onRefresh?.();
            }
            handlers.close();
        } catch (err: any) {
            on401Error(error, () => {
                logToServer(
                    'error',
                    `Unauthenticated access on /api/requests/internal-transfer. JWT may be invalid or expired.`
                );
            });
            setError(err?.response || err);
        }
        setIsLoading(false);
    };

    const onError = (e: FieldErrors) => console.log(e);

    useEffect(() => {
        if (opened && selectedTransferType?.value) {
            setError({ message: '' });
            reset({
                ...defaultValues,
                transferType: selectedTransferType,
                counterPartId: counterPartOptions?.length === 1 ? counterPartOptions[0] : selectedCounterPartId,
                currencyId: selectedTransferType?.value === TransferType.FIAT ? defaultCurrencyId : defaultAssetId,
                amount: undefined
            });
        }
    }, [selectedTransferType?.value]);

    useEffect(() => {
        if (opened && selectedCurrency?.value) {
            if (currentBalance <= 0) {
                setError({ message: `Insufficient balance for withdrawal` });
            } else {
                setError({ message: '' });
            }
            reset({
                ...defaultValues,
                transferType: selectedTransferType,
                currencyId: selectedCurrency,
                counterPartId: counterPartOptions?.length === 1 ? counterPartOptions[0] : selectedCounterPartId,
                amount: undefined
            });
        }
    }, [selectedCurrency?.value]);

    useEffect(() => {
        if (opened) {
            balancesApi.mutate();
            linkedEntitiesApi.mutate();
            setAbortController(new AbortController());
        } else {
            reset(defaultValues, { keepIsValid: false });
            setError({ message: '' });
            abortController.abort();
        }

        return () => abortController.abort();
    }, [opened]);

    return (
        <Modal {...props} className="h-auto" size="max-w-lg">
            <ModalHeader>
                <ModalTitle>{lang.portal.makeAnInternalTransfer}</ModalTitle>
                <ModalClose handlers={handlers} />
            </ModalHeader>
            <Divider neutral />
            <FormProvider {...formInstance}>
                <form onSubmit={handleSubmit(onSubmit, onError)} className="relative h-full w-full text-brand-text">
                    {/* <FormStepper.Main activeStep={0}>
                            {steps.map(({ label }, index) => (
                                <FormStepper.Step key={label} step={index} label={label} />
                            ))}
                        </FormStepper.Main> */}
                    <div className="flex flex-col gap-4 py-4 px-2 sm:px-4">
                        <div className="flex flex-col gap-4 text-sm">
                            <fieldset className="space-y-3">
                                {/* <legend className="text-xs text-brand-neutral-500">
                                        Internal Transfer Information
                                    </legend> */}
                                <PortalEntry
                                    label={lang.portal.availableBalance}
                                    value={availableBalance}
                                    className={cn('', {
                                        ['text-brand-neutral-200 text-sm']:
                                            !selectedCurrency?.name || availableBalance === 'No available balance'
                                    })}
                                    valueOnly
                                />
                                <div className="flex flex-col xsm:flex-row text-sm gap-4 w-full">
                                    <div className="flex-1">
                                        <PortalInputController
                                            name="transferType"
                                            label={lang.portal.transferType}
                                            placeholder="Transfer Type"
                                            required>
                                            <Select options={transferTypeOptions} />
                                        </PortalInputController>
                                    </div>
                                    <div className="flex-1">
                                        <PortalInputController
                                            name="currencyId"
                                            label={
                                                selectedTransferType?.value === 'FIAT'
                                                    ? lang.lite.currency
                                                    : lang.lite.asset
                                            }
                                            placeholder="Currency"
                                            required>
                                            <FilterSelect
                                                options={transferCcyOptions}
                                                enableCurrencyIcon
                                                enableSorting
                                            />
                                        </PortalInputController>
                                    </div>
                                </div>
                            </fieldset>
                            <div className="flex-1">
                                <PortalInputController
                                    name="amount"
                                    label={lang.portal.amountToTransfer}
                                    placeholder="0.00"
                                    required
                                    disabled={!selectedCurrency?.value}>
                                    <PortalNumberInput currency={selectedCurrency?.name} />
                                </PortalInputController>
                            </div>
                            <fieldset className="space-y-3">
                                <div className="flex flex-col xsm:flex-row text-sm gap-4 w-full">
                                    <div className="flex-1">
                                        <PortalEntry
                                            disabled
                                            label={lang.portal.fromAccount}
                                            value={foundEntity?.entityName}
                                        />
                                    </div>
                                    <div className="flex-1">
                                        <PortalInputController
                                            name="counterPartId"
                                            label={lang.portal.toAccount}
                                            placeholder="To Account"
                                            disabled={counterPartOptions?.length < 2}
                                            required>
                                            <Select options={counterPartOptions} enableSorting />
                                        </PortalInputController>
                                    </div>
                                </div>
                            </fieldset>

                            <PortalInputController
                                name="requestDescription"
                                label={lang.portal.requestDescription}
                                placeholder={lang.portal.requestDescription}
                                // alignStart
                            >
                                <TextArea rows={2} />
                            </PortalInputController>

                            <ErrorMessage error={error} />
                        </div>
                    </div>
                    <Divider neutral />
                    <div className="flex space-x-2 justify-end text-brand-text text-sm items-center p-2 sm:p-4">
                        <button
                            type="button"
                            className="w-auto rounded-md p-2 px-4 bg-brand-neutral-600 hover:bg-brand-neutral-500"
                            onClick={() => onClose()}>
                            {lang.common.cancel}
                        </button>
                        <Button
                            type="button"
                            className="w-auto rounded-md p-2 px-4 bg-brand-primary hover:bg-brand-primary-light"
                            onClick={() => {
                                if (!isLoading) handleSubmit(onSubmit, onError)();
                            }}
                            disabled={!isValid || currentBalance <= 0}
                            isLoading={isLoading}>
                            {lang.portal.submitRequest}
                        </Button>
                    </div>
                </form>
            </FormProvider>
        </Modal>
    );
};

export default PortalInternalTransferModal;

interface WhitelistErrorProps {
    type: 'FIAT' | 'CRYPTO';
    selectedCurrency: any;
}

export const WhitelistError = (props: WhitelistErrorProps) => {
    const navigate = useNavigate();
    const { type, selectedCurrency } = props;

    const name = type === 'FIAT' ? 'bank account' : 'wallet address';
    {
        /* TBA - Translations */
    }
    return (
        <div className="flex flex-col gap-3">
            <span>
                {/* TBA - Translations */}
                You currently don{`'`}t have a whitelisted {name} to proceed with the operation. Please submit a
                whitelist request with {selectedCurrency?.label} at the
                {type === 'FIAT' ? ' Bank Accounts' : ' Wallets'} page
                <button
                    onClick={() => {
                        if (type === 'FIAT') {
                            navigate('/portal/bank-accounts');
                        } else {
                            navigate('/portal/wallets');
                        }
                    }}
                    className="italic text-brand-neutral-200 hover:underline mx-1">
                    here
                </button>
                .
            </span>
        </div>
    );
};
